/**
 * The keys exercises can be generated in, and the staff placement of their
 * signature accidentals.
 *
 * The list is deliberately short: friendly signatures with at most two
 * accidentals, so the student reads the staff rather than decoding it.
 */

import type { Letter, Mode, MusicKey } from './types';
import { dOf } from './pitch';

/** Every key the trainer offers, majors first. */
export const KEYS: readonly MusicKey[] = [
  { id: 'C', label: 'C major', tonic: 'C', mode: 'major', sig: 0 },
  { id: 'G', label: 'G major', tonic: 'G', mode: 'major', sig: 1 },
  { id: 'D', label: 'D major', tonic: 'D', mode: 'major', sig: 2 },
  { id: 'F', label: 'F major', tonic: 'F', mode: 'major', sig: -1 },
  { id: 'Am', label: 'A minor', tonic: 'A', mode: 'minor', sig: 0 },
  { id: 'Dm', label: 'D minor', tonic: 'D', mode: 'minor', sig: -1 },
  { id: 'Em', label: 'E minor', tonic: 'E', mode: 'minor', sig: 1 },
  { id: 'Gm', label: 'G minor', tonic: 'G', mode: 'minor', sig: -2 },
];

/** Look a key up by id, falling back to C major so a bad save can still load. */
export const keyById = (id: string | undefined): MusicKey =>
  KEYS.find((k) => k.id === id) ?? KEYS[0];

/** The keys of one mode — used by the randomizer to keep the coin flip fair. */
export const keysOfMode = (mode: Mode): readonly MusicKey[] => KEYS.filter((k) => k.mode === mode);

/**
 * Where each signature accidental sits, as a diatonic index, per clef.
 *
 * These are the conventional engraving positions; sharps and flats have
 * different traditional shapes so both orders are spelled out.
 */
const SIGNATURE_POSITIONS: Record<'treble' | 'bass', Record<'sharp' | 'flat', readonly number[]>> = {
  treble: {
    sharp: (['F5', 'C5', 'G5', 'D5', 'A4', 'E5', 'B4'] as const).map(pos),
    flat: (['B4', 'E5', 'A4', 'D5', 'G4', 'C5', 'F4'] as const).map(pos),
  },
  bass: {
    sharp: (['F3', 'C3', 'G3', 'D3', 'A2', 'E3', 'B2'] as const).map(pos),
    flat: (['B2', 'E3', 'A2', 'D3', 'G2', 'C3', 'F2'] as const).map(pos),
  },
};

function pos(name: string): number {
  return dOf(name[0] as Letter, Number(name[1]));
}

/**
 * The diatonic positions to draw a key signature at, in engraving order.
 * Returns an empty array for C major / A minor.
 */
export function signaturePositions(sig: number, clef: 'treble' | 'bass'): readonly number[] {
  if (sig === 0) return [];
  const order = SIGNATURE_POSITIONS[clef][sig > 0 ? 'sharp' : 'flat'];
  return order.slice(0, Math.abs(sig));
}

/** The glyph a signature is drawn with. */
export const signatureGlyph = (sig: number): '♯' | '♭' => (sig > 0 ? '♯' : '♭');
